import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, GeoJSON, ZoomControl } from 'react-leaflet';
import { Loader2, MapPin, Euro, AlertTriangle, TrendingDown, Building2, X, ArrowRight } from 'lucide-react';
import api from '../api/client';
import 'leaflet/dist/leaflet.css';

// Normaliza el nombre de provincia para hacer match: minúsculas, sin acentos y
// aliases conocidos (BE has «MADRID»; GeoJSON has «Madrid»; Balears/Baleares).
const _norm = (s) => (s || '')
  .toString().toLowerCase()
  .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  .replace(/^(a|la|el|las|los)\s+/, '')
  .replace(/\s+/g, ' ')
  .trim();
const ALIAS = {
  'illes balears': 'baleares',
  'balears illes': 'baleares',
  'islas baleares': 'baleares',
  'islas canarias': 'canarias',
  'las palmas': 'las palmas',
  'santa cruz de tenerife': 'santa cruz de tenerife',
  'gipuzkoa': 'guipuzcoa',
  'bizkaia': 'vizcaya',
  'araba/alava': 'alava',
  'ourense': 'orense',
  'a coruna': 'coruna',
  'a coruña': 'coruna',
  'la coruña': 'coruna',
  'castello': 'castellon',
  'castello/castellon': 'castellon',
  'lleida': 'lerida',
  'girona': 'gerona',
  'palencia': 'palencia',
  'valencia/valencia': 'valencia',
  'valencia/valència': 'valencia',
  'alicante/alacant': 'alicante',
};
const canonical = (name) => {
  const n = _norm(name);
  return ALIAS[n] || n;
};

const eur = (n) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const fmt = (n) => new Intl.NumberFormat('es-ES').format(n || 0);

const METRICS = [
  { key: 'portfolio_eur',    label: 'Valor cartera (€/año)',  color: '#A3FF12', format: (v) => eur(v) },
  { key: 'sites',            label: 'Nº emplazamientos',       color: '#60a5fa', format: (v) => fmt(v) },
  { key: 'incidents_12m',    label: 'Incidencias 12 m',        color: '#f87171', format: (v) => fmt(v) },
  { key: 'expiring_12m',     label: 'Vencimientos 12 m',       color: '#fbbf24', format: (v) => fmt(v) },
  { key: 'expiring_12m_eur', label: 'Riesgo €/año 12 m',       color: '#fb923c', format: (v) => eur(v) },
  { key: 'with_owner',       label: 'Con propietario',         color: '#34d399', format: (v) => fmt(v) },
];

// Interpola un color entre "vacío" (transparente) y `base` según ratio 0-1.
const hexToRgb = (hex) => {
  const m = hex.replace('#', '');
  return [parseInt(m.slice(0, 2), 16), parseInt(m.slice(2, 4), 16), parseInt(m.slice(4, 6), 16)];
};
const colorFor = (ratio, baseHex) => {
  const [r, g, b] = hexToRgb(baseHex);
  const a = 0.15 + Math.min(1, Math.max(0, ratio)) * 0.75;
  return `rgba(${r},${g},${b},${a.toFixed(2)})`;
};

const HeatMapNacional = () => {
  const [data, setData] = useState(null);
  const [geo, setGeo] = useState(null);
  const [metricKey, setMetricKey] = useState('portfolio_eur');
  const [loading, setLoading] = useState(true);
  const [hover, setHover] = useState(null);
  const navigate = useNavigate();
  // Guardamos la última capa resaltada para poder "des-resaltarla" cuando
  // el ratón entra en otra provincia (highlight persistente).
  const lastHoverLayerRef = React.useRef(null);

  const goToProvince = (provinceName) => {
    if (!provinceName) return;
    navigate(`/app/emplazamientos?prov=${encodeURIComponent(provinceName)}`);
  };

  useEffect(() => {
    (async () => {
      try {
        const [d, g] = await Promise.all([
          api.get('/dashboard/heatmap-provinces'),
          fetch(`${process.env.PUBLIC_URL || ''}/spain_provinces.geojson`).then((r) => r.json()),
        ]);
        setData(d.data);
        setGeo(g);
      } finally { setLoading(false); }
    })();
  }, []);

  const metric = METRICS.find((m) => m.key === metricKey);

  // Mapa canonical(name) → item
  const byProvince = useMemo(() => {
    if (!data) return {};
    const map = {};
    data.items.forEach((it) => { map[canonical(it.province)] = it; });
    return map;
  }, [data]);

  const maxValue = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, ...data.items.map((i) => Number(i[metricKey]) || 0));
  }, [data, metricKey]);

  const styleFeature = (feature) => {
    const item = byProvince[canonical(feature.properties.name)];
    const v = Number(item?.[metricKey]) || 0;
    const ratio = v / maxValue;
    return {
      color: '#111',
      weight: 0.8,
      fillColor: colorFor(ratio, metric.color),
      fillOpacity: 1,
    };
  };

  const onEachFeature = (feature, layer) => {
    const item = byProvince[canonical(feature.properties.name)];
    const baseStyle = styleFeature(feature);
    // Guardamos el estilo base en la propia capa para poder restaurarlo cuando
    // otra capa gane el foco (highlight persistente hasta hover en otra).
    layer._baseStyle = baseStyle;
    layer.on({
      mouseover: (e) => {
        const target = e.target;
        // Si ya hay otra provincia resaltada, la reseteamos primero.
        const prev = lastHoverLayerRef.current;
        if (prev && prev !== target) {
          prev.setStyle(prev._baseStyle);
        }
        target.setStyle({
          weight: 2.5,
          color: '#ffffff',
          fillColor: metric.color,
          fillOpacity: 0.95,
        });
        target.bringToFront();
        lastHoverLayerRef.current = target;
        setHover({ name: feature.properties.name, item });
      },
      // No reseteamos en mouseout: el highlight se mantiene "posado" hasta que
      // el usuario entre en otra provincia.
      click: () => {
        goToProvince(item?.province || feature.properties.name);
      },
    });
    if (item) {
      layer.bindTooltip(
        `<div style="font-family:system-ui;font-size:12px;color:#f8fafc;line-height:1.55">
          <div style="font-weight:900;color:#ffffff;font-size:14px;border-bottom:1px solid rgba(255,255,255,0.15);padding-bottom:4px;margin-bottom:4px">
            ${feature.properties.name}
          </div>
          <div><span style="color:#94a3b8">Sites:</span> <b style="color:#ffffff">${fmt(item.sites)}</b></div>
          <div><span style="color:#94a3b8">Cartera:</span> <b style="color:#A3FF12">${eur(item.portfolio_eur)}</b> <span style="color:#94a3b8">/año</span></div>
          <div><span style="color:#94a3b8">Venc. 12m:</span> <b style="color:#fbbf24">${fmt(item.expiring_12m)}</b> · ${eur(item.expiring_12m_eur)}</div>
          <div><span style="color:#94a3b8">Incidencias 12m:</span> <b style="color:#f87171">${fmt(item.incidents_12m)}</b></div>
          <div style="margin-top:6px;padding-top:5px;border-top:1px solid rgba(255,255,255,0.1);color:#A3FF12;font-weight:800;font-size:11px">→ CLIC PARA VER LOS SITES</div>
        </div>`,
        { sticky: true, className: 'heatmap-tt', interactive: false, opacity: 1, direction: 'top', offset: [0, -6] },
      );
    }
  };

  const geoJsonKey = `${metricKey}-${!!geo}`;

  if (loading || !data || !geo) return (
    <div className="p-10 flex justify-center items-center min-h-[60vh]"><Loader2 className="animate-spin text-[#A3FF12]" size={32}/></div>
  );

  const t = data.totals;

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto" data-testid="heatmap-nacional">
      <div className="mb-4">
        <div className="text-xs font-bold tracking-widest text-[#A3FF12]">MAPA · CARTERA NACIONAL</div>
        <h1 className="text-3xl sm:text-4xl font-black mt-1">Heatmap por provincia</h1>
        <p className="text-white/50 text-sm mt-1">{fmt(t.provinces)} provincias · {fmt(t.sites)} sites · {eur(t.portfolio_eur)}/año · {fmt(t.expiring_12m)} venc. 12m · {fmt(t.incidents_12m)} inc. 12m.</p>
      </div>

      {/* Selector métrica */}
      <div className="flex flex-wrap gap-2 mb-4">
        {METRICS.map((m) => (
          <button
            key={m.key}
            onClick={() => setMetricKey(m.key)}
            data-testid={`heatmap-metric-${m.key}`}
            className={`px-3 py-1.5 rounded-lg text-[11px] font-black tracking-wide border transition-colors ${
              metricKey === m.key
                ? 'bg-white/10 text-white border-white/30'
                : 'text-white/50 border-white/10 hover:text-white hover:border-white/25'
            }`}
            style={metricKey === m.key ? { boxShadow: `inset 0 -2px 0 ${m.color}` } : {}}
          >
            <span className="w-2 h-2 inline-block rounded-full mr-2" style={{ background: m.color }}/>
            {m.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        {/* Mapa */}
        <div className="bg-black/40 border border-white/10 rounded-2xl overflow-hidden" style={{ height: 620 }}>
          <MapContainer
            center={[40.0, -3.7]}
            zoom={6}
            minZoom={5}
            maxZoom={9}
            style={{ height: '100%', width: '100%', background: '#0a0a0a' }}
            zoomControl={false}
            attributionControl={false}
          >
            <GeoJSON key={geoJsonKey} data={geo} style={styleFeature} onEachFeature={onEachFeature}/>
            <ZoomControl position="bottomright"/>
          </MapContainer>
        </div>

        {/* Panel ranking */}
        <div className="bg-black/40 border border-white/10 rounded-2xl overflow-hidden flex flex-col" style={{ maxHeight: 620 }}>
          <div className="px-4 py-3 border-b border-white/10 sticky top-0 bg-black/50">
            <div className="text-[10px] font-black uppercase tracking-widest text-white/50">Ranking</div>
            <div className="text-sm font-black text-white">{metric.label}</div>
          </div>
          <div className="overflow-y-auto text-xs">
            {data.items
              .filter((i) => i.province !== 'Sin provincia')
              .slice()
              .sort((a, b) => (Number(b[metricKey]) || 0) - (Number(a[metricKey]) || 0))
              .map((i, idx) => {
                const v = Number(i[metricKey]) || 0;
                const ratio = v / maxValue;
                return (
                  <button
                    key={i.province}
                    onClick={() => goToProvince(i.province)}
                    data-testid={`ranking-row-${i.province}`}
                    className="w-full flex items-center gap-2 px-3 py-2 border-b border-white/5 hover:bg-white/[0.06] text-left group"
                    title={`Ver los ${fmt(i.sites)} sites de ${i.province}`}
                  >
                    <div className="w-5 text-[10px] font-mono text-white/40">{idx + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-white/90 truncate group-hover:text-[#A3FF12] transition-colors">{i.province}</div>
                      <div className="text-white/40 text-[10px]">{fmt(i.sites)} sites · {eur(i.portfolio_eur)}/año</div>
                    </div>
                    <div className="text-right font-black" style={{ color: metric.color }}>
                      {metric.format(v)}
                    </div>
                    <div className="w-14 h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full" style={{ width: `${Math.min(100, ratio * 100)}%`, background: metric.color }}/>
                    </div>
                    <ArrowRight size={12} className="text-white/20 group-hover:text-[#A3FF12] transition-colors"/>
                  </button>
                );
              })}
          </div>
        </div>
      </div>

      {/* Info detallada de la provincia bajo hover */}
      {hover?.item && (
        <div className="mt-3 bg-black/70 border border-[#A3FF12]/30 rounded-xl px-4 py-3 flex items-center gap-4 flex-wrap shadow-lg">
          <div className="flex items-center gap-2 font-black text-white text-base">
            <MapPin size={15} className="text-[#A3FF12]"/> {hover.name}
          </div>
          <span className="flex items-center gap-1.5 text-sm text-white">
            <Building2 size={12} className="text-white/60"/>
            <b className="text-white font-black">{fmt(hover.item.sites)}</b>
            <span className="text-white/60">sites</span>
          </span>
          <span className="flex items-center gap-1.5 text-sm">
            <Euro size={12} className="text-[#A3FF12]"/>
            <b className="text-[#A3FF12] font-black">{eur(hover.item.portfolio_eur)}</b>
            <span className="text-white/60">/año</span>
          </span>
          <span className="flex items-center gap-1.5 text-sm">
            <TrendingDown size={12} className="text-orange-300"/>
            <span className="text-white/60">Venc 12m:</span>
            <b className="text-orange-200 font-black">{fmt(hover.item.expiring_12m)}</b>
            <span className="text-white/60">·</span>
            <b className="text-orange-300 font-black">{eur(hover.item.expiring_12m_eur)}</b>
          </span>
          <span className="flex items-center gap-1.5 text-sm">
            <AlertTriangle size={12} className="text-red-300"/>
            <span className="text-white/60">Incidencias 12m:</span>
            <b className="text-red-300 font-black">{fmt(hover.item.incidents_12m)}</b>
          </span>
        </div>
      )}
    </div>
  );
};

export default HeatMapNacional;
